import { Observable, of, merge, fromEventPattern, EMPTY } from 'rxjs';
import { filter, map, switchMap, distinctUntilChanged, withLatestFrom, take } from 'rxjs/operators';
import algoliaAnalytics from 'search-insights';
import { EpicDeps } from '@wearejh/m2-pwa-engine/lib/types';
import { AppendUserState } from '@wearejh/m2-pwa-user';

import { sanitizeAlgoliaToken } from 'src/components/Algolia/utils/sanitizeAlgoliaToken';
import { CookieBannerStore } from 'src/features/cookie-banner/types';

import { AlgoliaEvents } from '../actions';
import { AlgoliaStore } from '../types';

export function algoliaAnalyticsEpic(
    _action$: Observable<any>,
    state$: Observable<AppendUserState & AlgoliaStore & CookieBannerStore>,
    deps: EpicDeps,
): Observable<any> {
    /**
     * Listen for the Cookie Banner values to determine if Algolia
     * Analytics should be initialized.
     */
    const cookieConsent$ = state$.pipe(
        map((state) => ({
            isReady: state.cookieBanner?.ready === true,
            hasConsented: state.cookieBanner?.services?.algolia_analytics === true,
        })),
        distinctUntilChanged((prev, curr) => prev.isReady === curr.isReady && prev.hasConsented === curr.hasConsented),
    );

    /**
     * Check if Algolia Analytics is initialized.
     */
    const isInitialized$ = state$.pipe(
        map((state) => state.algolia?.analytics?.initialised || false),
        distinctUntilChanged(),
    );

    /**
     * Initialize Algolia Analytics.
     *
     * Note: Defaults to user has opted out, so it will not track
     * any events until the user consents.
     */
    const initialize$ = of(true).pipe(
        take(1),
        switchMap(() => {
            const apiKey = deps.env['ALGOLIA_API_KEY'];
            const appId = deps.env['ALGOLIA_APP_ID'];

            if (!apiKey || !appId) {
                return EMPTY;
            }

            /**
             * Initialize Algolia Analytics with user consent disabled as default.
             * It's important to use the `partial: true` option so that
             * Algolia Analytics knows to expect a future consent change.
             */
            algoliaAnalytics('init', {
                apiKey,
                appId,
                partial: true,
                useCookie: false,
                userHasOptedOut: true,
            });

            // Get the anonymous user token from Algolia if available
            let anoymousUserToken;

            try {
                anoymousUserToken = algoliaAnalytics('getUserToken');
            } catch (error) {
                console.error('Error getting Algolia user token:', error);
            }

            return of({
                type: AlgoliaEvents.ANALYTICS_INITIALISE,
                payload: { anoymousUserToken },
            });
        }),
    );

    /**
     * When the Cookie Banner consent changes, update Algolia Analytics.
     */
    const consentChanged$ = cookieConsent$.pipe(
        // Wait for ready to be true first
        filter(({ isReady }) => isReady),
        withLatestFrom(isInitialized$),

        // Only proceed if Algolia is initialized
        filter(([_, isInitialized]) => isInitialized),

        // Only react to consent changes
        map(([{ hasConsented }]) => hasConsented),
        distinctUntilChanged(),

        switchMap((hasUserConsented) => {
            /**
             * Re-initialize Algolia Analytics with the new consent state.
             */
            algoliaAnalytics('init', {
                partial: true,
                useCookie: hasUserConsented,
                userHasOptedOut: !hasUserConsented,
            });

            let anoymousUserToken;

            if (hasUserConsented) {
                try {
                    anoymousUserToken = algoliaAnalytics('getUserToken');
                } catch (error) {
                    console.error('Error getting Algolia user token:', error);
                }
            }

            // Return actions to update the Redux state
            return of({
                type: AlgoliaEvents.ANALYTICS_CONSENT_CHANGED,
                payload: { anoymousUserToken },
            });
        }),
    );

    /**
     * Listen for changes for the user status (guest vs user)
     */
    const userStatus$ = state$.pipe(
        map((state) => {
            return {
                isAlgoliaAnalyticsInitialized: state.algolia?.analytics?.initialised || false,
                isSignedIn: state.user?.session === 'SignedIn',
                authenticatedUserToken: sanitizeAlgoliaToken(state.user?.token),
            };
        }),
        distinctUntilChanged(
            (prev, curr) =>
                prev.isSignedIn === curr.isSignedIn &&
                prev.authenticatedUserToken === curr.authenticatedUserToken &&
                prev.isAlgoliaAnalyticsInitialized === curr.isAlgoliaAnalyticsInitialized,
        ),
    );

    /**
     * Set the authenticated user token in Algolia Analytics
     * when the user signs in or out.
     * If the user is signed out, unset the token.
     * If the user is signed in, set the token.
     *
     * It's important to note that this token is different from the
     * user token generated by Algolia Analytics. Both values will be
     * used in the analytics events so that Algolia can merge the
     * session data. This allows Algolia to track the user across sessions.
     */
    const updateAuthenticatedUserToken$ = userStatus$.pipe(
        // Wait for Algolia Analytics to be initialized
        filter(({ isAlgoliaAnalyticsInitialized }) => isAlgoliaAnalyticsInitialized),
        switchMap(({ isSignedIn, authenticatedUserToken }) => {
            if (isSignedIn && authenticatedUserToken) {
                algoliaAnalytics('setAuthenticatedUserToken', authenticatedUserToken);

                return of({
                    type: AlgoliaEvents.ANALYTICS_SET_AUTHENTICATED_USER,
                    payload: { authenticatedUserToken },
                });
            }

            algoliaAnalytics('setAuthenticatedUserToken', undefined);

            return of({ type: AlgoliaEvents.ANALYTICS_UNSET_AUTHENTICATED_USER });
        }),
    );

    /**
     * Listen for changes to the anonymous user token.
     *
     * We store the anonymous token in Redux so that it can be
     * used when required.
     */
    const anoymousUserTokenChange$ = fromEventPattern(
        (handler) => {
            algoliaAnalytics('onUserTokenChange', handler);
            return handler;
        },
        () => {},
    ).pipe(
        withLatestFrom(isInitialized$),
        filter(([_, isInitialized]) => isInitialized),
        map(([anonymousUserToken]) => {
            return {
                type: AlgoliaEvents.ANALYTICS_SET_ANONYMOUS_USER_TOKEN,
                payload: { anonymousUserToken },
            };
        }),
    );

    return merge(initialize$, consentChanged$, updateAuthenticatedUserToken$, anoymousUserTokenChange$);
}

export default algoliaAnalyticsEpic;
